import { gql, type TypedDocumentNode } from "@apollo/client";
import type { CommitteeAssignmentStatus, CommitteeStatus } from "./types";

export type BackofficeCommitteeMutationResult = {
  success: boolean;
  message: string;
  committee: {
    id: string;
    name: string;
    status: CommitteeStatus;
    core: boolean;
  } | null;
};

export type CreateBackofficeCommitteeMutationData = {
  createBackofficeCommittee: BackofficeCommitteeMutationResult;
};

export type CreateBackofficeCommitteeMutationVariables = {
  input: {
    name: string;
    sectionId: string;
    status?: CommitteeStatus;
    core?: boolean;
  };
};

export const CREATE_BACKOFFICE_COMMITTEE_MUTATION: TypedDocumentNode<
  CreateBackofficeCommitteeMutationData,
  CreateBackofficeCommitteeMutationVariables
> = gql`
  mutation CreateBackofficeCommittee($input: BackofficeCreateCommitteeInput!) {
    createBackofficeCommittee(input: $input) {
      success
      message
      committee {
        id
        name
        status
        core
      }
    }
  }
`;

export type UpdateBackofficeCommitteeMutationData = {
  updateBackofficeCommittee: BackofficeCommitteeMutationResult;
};

export type UpdateBackofficeCommitteeMutationVariables = {
  input: {
    id: string;
    name?: string;
    status?: CommitteeStatus;
    core?: boolean;
  };
};

export const UPDATE_BACKOFFICE_COMMITTEE_MUTATION: TypedDocumentNode<
  UpdateBackofficeCommitteeMutationData,
  UpdateBackofficeCommitteeMutationVariables
> = gql`
  mutation UpdateBackofficeCommittee($input: BackofficeUpdateCommitteeInput!) {
    updateBackofficeCommittee(input: $input) {
      success
      message
      committee {
        id
        name
        status
        core
      }
    }
  }
`;

export type UpdateBackofficeCommitteeAssignmentMutationData = {
  updateBackofficeCommitteeAssignment: {
    success: boolean;
    message: string;
    committeeAssignment: {
      id: string;
      startDate: string | null;
      endDate: string | null;
      status: CommitteeAssignmentStatus;
      title: {
        id: string;
        title: string;
      };
    } | null;
  };
};

export type UpdateBackofficeCommitteeAssignmentMutationVariables = {
  input: {
    id: string;
    titleId?: string;
    startDate?: string | null;
    endDate?: string | null;
  };
};

export const UPDATE_BACKOFFICE_COMMITTEE_ASSIGNMENT_MUTATION: TypedDocumentNode<
  UpdateBackofficeCommitteeAssignmentMutationData,
  UpdateBackofficeCommitteeAssignmentMutationVariables
> = gql`
  mutation UpdateBackofficeCommitteeAssignment($input: BackofficeUpdateCommitteeAssignmentInput!) {
    updateBackofficeCommitteeAssignment(input: $input) {
      success
      message
      committeeAssignment {
        id
        startDate
        endDate
        status
        title {
          id
          title
        }
      }
    }
  }
`;

export type BulkCreateCommitteeAssignmentAttributes = {
  personId: string;
  titleId: string;
  startDate?: string | null;
  endDate?: string | null;
};

export type BulkCreateBackofficeCommitteeAssignmentsMutationData = {
  bulkCreateBackofficeCommitteeAssignments: {
    success: boolean;
    message: string;
    successfulAssignments: { id: string; person: { id: string } }[] | null;
    failedAssignments:
      | {
          personId: string;
          titleId: string | null;
          message: string;
        }[]
      | null;
  };
};

export type BulkCreateBackofficeCommitteeAssignmentsMutationVariables = {
  input: {
    committeeId: string;
    assignments: BulkCreateCommitteeAssignmentAttributes[];
  };
};

export const BULK_CREATE_BACKOFFICE_COMMITTEE_ASSIGNMENTS_MUTATION: TypedDocumentNode<
  BulkCreateBackofficeCommitteeAssignmentsMutationData,
  BulkCreateBackofficeCommitteeAssignmentsMutationVariables
> = gql`
  mutation BulkCreateBackofficeCommitteeAssignments($input: BackofficeBulkCreateCommitteeAssignmentsInput!) {
    bulkCreateBackofficeCommitteeAssignments(input: $input) {
      success
      message
      successfulAssignments {
        id
        person {
          id
        }
      }
      failedAssignments {
        personId
        titleId
        message
      }
    }
  }
`;

export type ExportBackofficeCommitteeAssignmentsMutationData = {
  exportBackofficeCommitteeAssignments: {
    success: boolean;
    message: string;
    url: string;
  };
};

export type ExportBackofficeCommitteeAssignmentsMutationVariables = {
  input: {
    committeeIds: string[];
  };
};

export const EXPORT_BACKOFFICE_COMMITTEE_ASSIGNMENTS_MUTATION: TypedDocumentNode<
  ExportBackofficeCommitteeAssignmentsMutationData,
  ExportBackofficeCommitteeAssignmentsMutationVariables
> = gql`
  mutation ExportBackofficeCommitteeAssignments($input: BackofficeExportCommitteeAssignmentsInput!) {
    exportBackofficeCommitteeAssignments(input: $input) {
      success
      message
      url
    }
  }
`;
